import type AudioProcessor from '@twilio/voice-sdk/es5/twilio/audioprocessor';

// HMSKrispPlugin is the public surface of 100ms's Krisp noise-cancellation
// plugin. We only ever touch its documented HMSAudioPlugin API
// (`init`, `processAudioTrack`, `toggle`, `setNoiseSuppressionLevel`,
// `stop`) — see the Krisp TLA (Exhibit E §3(c)): using only this public
// surface is integration, not adaptation.
type HMSKrispPlugin = import('@100mslive/hms-noise-cancellation').HMSKrispPlugin;

// Lazy-loaded so neither the ~MBs of Krisp WASM glue nor the hms-video-store
// peer dep land in the initial bundle. The first call that needs noise
// cancellation pays the import cost.
async function loadKrispPlugin(): Promise<HMSKrispPlugin> {
  const mod = await import('@100mslive/hms-noise-cancellation');
  return new mod.HMSKrispPlugin();
}

export class KrispAudioProcessor implements AudioProcessor {
  private plugin: HMSKrispPlugin | null = null;
  private audioContext: AudioContext | null = null;
  private destination: MediaStreamAudioDestinationNode | null = null;

  async createProcessedStream(stream: MediaStream): Promise<MediaStream> {
    this.plugin = await loadKrispPlugin();

    if (!this.plugin.checkSupport().isSupported) {
      // Browser can't run the Krisp worklet (e.g. no AudioWorklet, no WASM).
      // Returning the raw stream lets the call proceed without NC.
      this.plugin = null;
      return stream;
    }

    await this.plugin.init();

    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(stream);
    const filterNode = await this.plugin.processAudioTrack(this.audioContext, source);

    this.destination = this.audioContext.createMediaStreamDestination();
    filterNode.connect(this.destination);

    return this.destination.stream;
  }

  async destroyProcessedStream(_stream: MediaStream): Promise<void> {
    this.plugin?.stop();
    this.plugin = null;

    this.destination?.disconnect();
    this.destination = null;

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }
  }

  /**
   * Insert Krisp into a caller-owned AudioContext graph: routes `source`
   * through the Krisp filter and returns the processed output node to wire
   * downstream. Returns null when the browser can't run Krisp, so the caller
   * can fall back to the raw source.
   *
   * Unlike {@link createProcessedStream}, this reuses the caller's context and
   * avoids a MediaStream round-trip — the shape the call-scheduler MicCapture
   * `createProcessingNode` hook needs for the proxied (WebSocket) call path.
   */
  async createProcessingNode(ctx: AudioContext, source: AudioNode): Promise<AudioNode | null> {
    this.plugin = await loadKrispPlugin();

    if (!this.plugin.checkSupport().isSupported) {
      this.plugin = null;
      return null;
    }

    await this.plugin.init();
    // processAudioTrack wires source → Krisp internally and returns the
    // processed output node; the caller connects that to the next node.
    return this.plugin.processAudioTrack(ctx, source);
  }

  /**
   * Tear down a graph started via {@link createProcessingNode}. The
   * AudioContext is owned by the caller (MicCapture), so we only stop the
   * plugin — we do not close a context here.
   */
  stopProcessingNode(): void {
    this.plugin?.stop();
    this.plugin = null;
  }

  isEnabled(): boolean {
    return this.plugin?.isEnabled() ?? false;
  }

  toggle(): void {
    this.plugin?.toggle();
  }

  setNoiseSuppressionLevel(level: number): void {
    this.plugin?.setNoiseSuppressionLevel(level);
  }
}
